import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Colophon: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl font-bold mb-8 text-center text-indigo-600 dark:text-indigo-400">
          {t('colophon.title')}
        </h1>
        
        <div className="space-y-8">
          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.about.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {t('colophon.about.content')}
            </p>
          </section>

          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.technologies.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-3 text-indigo-600 dark:text-indigo-400">Frontend</h3>
                <ul className="space-y-2">
                  {['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'React Icons', 'React Vertical Timeline'].map((tech) => (
                    <li key={tech} className="flex items-center space-x-2">
                      <span className="text-indigo-600 dark:text-indigo-400">•</span>
                      <span className="text-gray-600 dark:text-gray-300">{tech}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-3 text-indigo-600 dark:text-indigo-400">Build & Tools</h3>
                <ul className="space-y-2">
                  {['Vite', 'React Router', 'i18next', 'ESLint', 'Prettier', 'PostCSS'].map((tech) => (
                    <li key={tech} className="flex items-center space-x-2">
                      <span className="text-indigo-600 dark:text-indigo-400">•</span>
                      <span className="text-gray-600 dark:text-gray-300">{tech}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.typography.title')}
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('colophon.typography.primary')}</p>
                <p className="text-xl font-sans">{t('colophon.typography.primaryValue')}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('colophon.typography.code')}</p>
                <p className="text-xl font-mono">{t('colophon.typography.codeValue')}</p>
              </div>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.colors.title')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { bg: 'bg-indigo-600 dark:bg-indigo-500', border: '', label: 'primary', value: 'Indigo 600' },
                { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border border-gray-200 dark:border-gray-700', label: 'background', value: 'lightDark' },
                { bg: 'bg-white dark:bg-gray-900', border: 'border border-gray-200 dark:border-gray-700', label: 'card', value: 'whiteDark' },
                { bg: 'bg-green-100 dark:bg-green-900', border: 'border border-green-200 dark:border-green-800', label: 'success', value: 'Green 100/900' }
              ].map((color, index) => (
                <div key={index}>
                  <div className={`h-16 rounded-md mb-2 ${color.bg} ${color.border}`}></div>
                  <p className="text-center text-sm text-gray-600 dark:text-gray-300">
                    {t(`colophon.colors.${color.label}`)}
                  </p>
                  <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                    {color.label === 'primary' ? 'Indigo 600' : color.label === 'success' ? 'Grün 100/900' : t(`colophon.colors.${color.value}`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.performance.title')}
            </h2>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              {[0, 1, 2, 3].map((index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-indigo-600 dark:text-indigo-400">•</span>
                  <span>{t(`colophon.performance.items.${index}`)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">
              {t('colophon.accessibility.title')}
            </h2>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              {[0, 1, 2, 3].map((index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-indigo-600 dark:text-indigo-400">•</span>
                  <span>{t(`colophon.accessibility.items.${index}`)}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="text-center text-sm text-gray-500 dark:text-gray-400 pt-4">
            <p>{t('colophon.lastUpdated')}: {new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Colophon;
